/**
 * DashboardSection — Lightweight section header for organizing operational zones.
 *
 * Provides a compact, uppercase section label with consistent spacing.
 * Supports optional collapse when all children are hidden.
 * Supports optional collapsible toggle with localStorage persistence.
 * Optional `updatedAt` prop shows a live "Updated Xs ago" indicator.
 */
import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface DashboardSectionProps {
    label: string;
    children: ReactNode;
    /** If true, section is entirely hidden (no header, no children) */
    hidden?: boolean;
    /** Enable collapsible toggle with chevron */
    collapsible?: boolean;
    /** localStorage key for persisting collapse state */
    storageKey?: string;
    /** Default expanded state (only used on first visit or if storageKey is absent) */
    defaultExpanded?: boolean;
    /** When provided, shows a live "Updated Xs ago" indicator */
    updatedAt?: Date;
}

function readStorage(key: string | undefined, fallback: boolean): boolean {
    if (!key) return fallback;
    try {
        const val = localStorage.getItem(key);
        if (val === 'true') return true;
        if (val === 'false') return false;
    } catch { /* SSR or quota */ }
    return fallback;
}

/**
 * Reusable hook for panels that want to embed collapse behavior
 * directly inside their own header instead of using CollapsibleBlock.
 */
export function useCollapsible(storageKey: string, defaultExpanded = true) {
    const [expanded, setExpanded] = useState(() => readStorage(storageKey, defaultExpanded));

    const toggle = useCallback(() => {
        setExpanded(prev => {
            const next = !prev;
            try { localStorage.setItem(storageKey, String(next)); } catch { /* quota */ }
            return next;
        });
    }, [storageKey]);

    return { expanded, toggle };
}

/**
 * Live "Updated Xs ago" hook — ticks every second.
 */
function useUpdatedAgo(updatedAt: Date | undefined): string | null {
    const [label, setLabel] = useState<string | null>(null);

    useEffect(() => {
        if (!updatedAt) { setLabel(null); return; }

        const compute = () => {
            const diffSec = Math.max(0, Math.floor((Date.now() - updatedAt.getTime()) / 1000));
            if (diffSec < 3) return 'Updated just now';
            if (diffSec < 60) return `Updated ${diffSec}s ago`;
            const mins = Math.floor(diffSec / 60);
            return `Updated ${mins}m ago`;
        };

        setLabel(compute());
        const id = setInterval(() => setLabel(compute()), 1000);
        return () => clearInterval(id);
    }, [updatedAt]);

    return label;
}

export function DashboardSection({
    label,
    children,
    hidden,
    collapsible,
    storageKey,
    defaultExpanded = true,
    updatedAt,
}: DashboardSectionProps) {
    const [expanded, setExpanded] = useState(() => readStorage(storageKey, defaultExpanded));
    const agoLabel = useUpdatedAgo(updatedAt);

    const toggle = useCallback(() => {
        setExpanded(prev => {
            const next = !prev;
            if (storageKey) {
                try { localStorage.setItem(storageKey, String(next)); } catch { /* quota */ }
            }
            return next;
        });
    }, [storageKey]);

    if (hidden) return null;

    return (
        <section aria-label={label}>
            <div className="flex items-center gap-3 mb-3">
                {collapsible ? (
                    <button
                        onClick={toggle}
                        className="flex items-center gap-1.5 group focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                        aria-expanded={expanded}
                        aria-controls={storageKey ? `section-${storageKey}` : undefined}
                    >
                        {expanded
                            ? <ChevronDown className="w-3 h-3 text-muted-foreground group-hover:text-foreground transition-colors" />
                            : <ChevronRight className="w-3 h-3 text-muted-foreground group-hover:text-foreground transition-colors" />
                        }
                        <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em] group-hover:text-foreground transition-colors">
                            {label}
                        </h2>
                    </button>
                ) : (
                    <h2 className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em]">
                        {label}
                    </h2>
                )}
                <div className="flex-1 h-px bg-border/40" />
                {agoLabel && (
                    <span className="text-[9px] font-medium text-muted-foreground/60 tabular-nums shrink-0">
                        {agoLabel}
                    </span>
                )}
            </div>
            {(!collapsible || expanded) && (
                <div
                    className="space-y-3"
                    id={storageKey ? `section-${storageKey}` : undefined}
                >
                    {children}
                </div>
            )}
        </section>
    );
}

// ─── Nested Collapsible Block ─────────────────────────────────────────────────
// Lighter-weight collapsible for individual items within a section.

interface CollapsibleBlockProps {
    label: string;
    children: ReactNode;
    storageKey: string;
    defaultExpanded?: boolean;
}

export function CollapsibleBlock({
    label,
    children,
    storageKey,
    defaultExpanded = true,
}: CollapsibleBlockProps) {
    const [expanded, setExpanded] = useState(() => readStorage(storageKey, defaultExpanded));

    const toggle = useCallback(() => {
        setExpanded(prev => {
            const next = !prev;
            try { localStorage.setItem(storageKey, String(next)); } catch { /* quota */ }
            return next;
        });
    }, [storageKey]);

    return (
        <div>
            <button
                onClick={toggle}
                className="flex items-center gap-1.5 mb-2 group focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                aria-expanded={expanded}
                aria-controls={`block-${storageKey}`}
            >
                {expanded
                    ? <ChevronDown className="w-3 h-3 text-muted-foreground group-hover:text-foreground transition-colors" />
                    : <ChevronRight className="w-3 h-3 text-muted-foreground group-hover:text-foreground transition-colors" />
                }
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.12em] group-hover:text-foreground transition-colors">
                    {label}
                </span>
            </button>
            {expanded && (
                <div id={`block-${storageKey}`}>
                    {children}
                </div>
            )}
        </div>
    );
}
